<template>
  <div class="sub-category-manage-mobile">
    <!-- 顶部标题 -->
    <div class="page-header">
      <h2>{{ $t('wholesaler.SubCategoryManage.line5_text') }}</h2>
      <button class="btn-add" @click="showAddDialog">
        <i class="el-icon-plus"></i> 添加
      </button>
    </div>

    <!-- 一级分类筛选 -->
    <div class="filter-section">
      <div class="filter-label">{{ $t('wholesaler.SubCategoryManage.line13_text') }}</div>
      <div class="filter-options">
        <div 
          v-for="cat in categoryOptions" 
          :key="cat"
          class="filter-option"
          :class="{ active: selectedCategory === cat }"
          @click="selectCategory(cat)"
        >
          {{ cat }}
        </div>
      </div>
    </div>

    <!-- 分类列表 -->
    <div class="category-list">
      <div v-if="loading" class="loading-state">
        <div class="loading-icon">⏳</div>
        <p>{{ $t('common.loading') }}</p>
      </div>

      <div v-else-if="subCategories.length === 0" class="empty-state">
        <div class="empty-icon">📂</div>
        <p>{{ $t('wholesaler.SubCategoryManage.line36_text') }}</p>
        <button class="btn-add-empty" @click="showAddDialog">{{ $t('wholesaler.SubCategoryManage.line37_text') }}</button>
      </div>

      <div v-else class="list-content">
        <div v-for="item in subCategories" :key="item.id" class="category-card">
          <div class="card-header">
            <div class="category-info">
              <div class="category-name">{{ item.subCategoryName }}</div>
              <div class="category-parent">属于: {{ item.categoryName }}</div>
            </div>
            <div class="category-sort">排序: {{ item.sortOrder || 0 }}</div>
          </div>
          <div class="card-actions">
            <button class="btn-edit" @click="handleEdit(item)">
              <i class="el-icon-edit"></i> 编辑
            </button>
            <button class="btn-delete" @click="handleDelete(item)">
              <i class="el-icon-delete"></i> 删除
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 添加/编辑弹窗 -->
    <div v-if="dialogVisible" class="mobile-dialog" @click.self="dialogVisible = false">
      <div class="dialog-content">
        <div class="dialog-header">
          <h3>{{ dialogMode === 'add' ? '添加二级分类' : '编辑二级分类' }}</h3>
          <button class="dialog-close" @click="dialogVisible = false">×</button>
        </div>
        
        <div class="dialog-body">
          <div class="form-group">
            <label class="form-label">{{ $t('wholesaler.SubCategoryManage.line71_text') }} <span class="required">*</span></label>
            <select v-model="form.categoryName" class="form-select" :class="{ error: formErrors.categoryName }">
              <option value="">{{ $t('wholesaler.SubCategoryManage.line73_text') }}</option>
              <option v-for="cat in categoryOptions" :key="cat" :value="cat">{{ cat }}</option>
            </select>
            <span v-if="formErrors.categoryName" class="error-text">{{ formErrors.categoryName }}</span>
          </div>

          <div class="form-group">
            <label class="form-label">{{ $t('wholesaler.SubCategoryManage.line80_text') }} <span class="required">*</span></label>
            <input 
              v-model="form.subCategoryName" 
              type="text" 
              class="form-input"
              :class="{ error: formErrors.subCategoryName }"
              :placeholder="$t('wholesaler.SubCategoryManage.line86_placeholder')"
              maxlength="50"
            >
            <span v-if="formErrors.subCategoryName" class="error-text">{{ formErrors.subCategoryName }}</span>
          </div>

          <div class="form-group">
            <label class="form-label">{{ $t('wholesaler.SubCategoryManage.line93_text') }}</label>
            <input 
              v-model.number="form.sortOrder" 
              type="number" 
              class="form-input"
              :placeholder="$t('wholesaler.SubCategoryManage.line98_placeholder')"
              min="0"
              max="9999"
            >
          </div>
        </div>

        <div class="dialog-footer">
          <button class="btn-cancel" @click="dialogVisible = false">{{ $t('common.cancel') }}</button>
          <button class="btn-confirm" @click="handleSubmit">{{ $t('common.confirm') }}</button>
        </div>
      </div>
    </div>

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <button class="nav-item" @click="$router.push('/wholesaler/home')">
        <span class="nav-item-icon">🏠</span>
        <span>{{ $t('common.home') }}</span>
      </button>

      <button class="nav-item" @click="$router.push('/wholesaler/product')">
        <span class="nav-item-icon">📦</span>
        <span>{{ $t('common.products') }}</span>
      </button>

      <button class="nav-item" @click="$router.push('/wholesaler/wholesale-price-list')">
        <span class="nav-item-icon">💰</span>
        <span>{{ $t('wholesaler.Home.line172_text') }}</span>
      </button>

      <button class="nav-item" @click="$router.push('/wholesaler/orders')">
        <span class="nav-item-icon">📋</span>
        <span>{{ $t('common.orders') }}</span>
      </button>

      <button class="nav-item" @click="$router.push('/my-profile')">
        <span class="nav-item-icon">👤</span>
        <span>{{ $t('common.profile') }}</span>
      </button>
    </nav>
  </div>
</template>

<script>
import { getSubCategories, createSubCategory, updateSubCategory, deleteSubCategory } from '@/api/subCategory'
import api from '@/services/api'

export default {
  name: 'SubCategoryManage',
  
  data() {
    return {
      wholesalerId: null,
      subCategories: [],
      categoryOptions: [],
      selectedCategory: '',
      dialogVisible: false,
      dialogMode: 'add',
      loading: false,
      form: {
        id: null,
        categoryName: '',
        subCategoryName: '',
        sortOrder: 0
      },
      formErrors: {
        categoryName: '',
        subCategoryName: ''
      }
    }
  },
  
  created() {
    this.wholesalerId = localStorage.getItem('stored_wholesaler_id') || localStorage.getItem('wholesaler_id')
    if (!this.wholesalerId) {
      this.$message.error('未找到批发商ID')
      return
    }
    this.loadCategories()
  },
  
  methods: {
    selectCategory(cat) {
      this.selectedCategory = cat
      this.loadSubCategories()
    },

    async loadCategories() {
      try {
        // api 响应拦截器已解包 { code, data, message } → 返回的直接是 data 数组
        const res = await api.get('/product/category/list')
        const list = Array.isArray(res) ? res : (res?.data || [])
        this.categoryOptions = list.map(cat => cat.name)
      } catch (error) {
        console.error('加载分类列表失败:', error)
        this.$message.error('加载分类列表失败')
      }
    },
    
    async loadSubCategories() {
      if (!this.wholesalerId) {
        this.$message.error('未找到批发商ID')
        return
      }
      if (!this.selectedCategory) {
        this.subCategories = []
        return
      }
      this.loading = true
      try {
        const res = await getSubCategories(this.wholesalerId, this.selectedCategory)
        this.subCategories = Array.isArray(res) ? res : (res?.data || [])
      } catch (error) {
        this.$message.error('加载二级分类失败')
      } finally {
        this.loading = false
      }
    },
    
    showAddDialog() {
      this.dialogMode = 'add'
      this.form = {
        id: null,
        categoryName: this.selectedCategory || '',
        subCategoryName: '',
        sortOrder: 0
      }
      this.formErrors = { categoryName: '', subCategoryName: '' }
      this.dialogVisible = true
    },
    
    handleEdit(row) {
      this.dialogMode = 'edit'
      this.form = {
        id: row.id,
        categoryName: row.categoryName,
        subCategoryName: row.subCategoryName,
        sortOrder: row.sortOrder || 0
      }
      this.formErrors = { categoryName: '', subCategoryName: '' }
      this.dialogVisible = true
    },
    
    validateForm() {
      this.formErrors = { categoryName: '', subCategoryName: '' }
      let valid = true

      if (!this.form.categoryName) {
        this.formErrors.categoryName = '请选择一级分类'
        valid = false
      }

      if (!this.form.subCategoryName || !this.form.subCategoryName.trim()) {
        this.formErrors.subCategoryName = '请输入二级分类名称'
        valid = false
      } else if (this.form.subCategoryName.length > 50) {
        this.formErrors.subCategoryName = '名称不能超过50个字符'
        valid = false
      }

      return valid
    },
    
    async handleSubmit() {
      if (!this.wholesalerId) {
        this.$message.error('未找到批发商ID')
        return
      }

      if (!this.validateForm()) {
        return
      }
      
      try {
        if (this.dialogMode === 'add') {
          await createSubCategory(this.wholesalerId, this.form)
          this.$message.success('添加成功')
        } else {
          await updateSubCategory(this.wholesalerId, this.form.id, this.form)
          this.$message.success('更新成功')
        }
        
        this.dialogVisible = false
        this.loadSubCategories()
      } catch (error) {
        if (error.response && error.response.data) {
          this.$message.error(error.response.data.error || '操作失败')
        } else {
          this.$message.error('操作失败')
        }
      }
    },
    
    async handleDelete(row) {
      if (!this.wholesalerId) {
        this.$message.error('未找到批发商ID')
        return
      }
      
      const confirmed = await new Promise(resolve => {
        if (confirm(`确定要删除"${row.subCategoryName}"吗？`)) {
          resolve(true)
        } else {
          resolve(false)
        }
      })
      
      if (!confirmed) return
      
      try {
        await deleteSubCategory(this.wholesalerId, row.id)
        this.$message.success('删除成功')
        this.loadSubCategories()
      } catch (error) {
        this.$message.error('删除失败')
      }
    }
  }
}
</script>

<style scoped>
.sub-category-manage-mobile {
  min-height: 100vh;
  background: #f5f7fa;
  padding-bottom: calc(70px + env(safe-area-inset-bottom, 0px));
}

/* 顶部标题 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: calc(20px + env(safe-area-inset-top, 0px)) 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.page-header h2 {
  margin: 0;
  font-size: 18px;
  color: white;
  font-weight: 600;
}

.btn-add {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-add:active {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(0.95);
}

/* 筛选区域 */
.filter-section {
  background: white;
  padding: 16px;
  margin-bottom: 12px;
}

.filter-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 12px;
  font-weight: 500;
}

.filter-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-option {
  padding: 8px 16px;
  background: #f0f2f5;
  border-radius: 16px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid transparent;
}

.filter-option.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-color: transparent;
}

.filter-option:active {
  transform: scale(0.95);
}

/* 分类列表 */
.category-list {
  padding: 0 16px;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.loading-icon,
.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.loading-state p,
.empty-state p {
  margin: 0;
  font-size: 14px;
}

.btn-add-empty {
  margin-top: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 24px;
  font-size: 14px;
  cursor: pointer;
}

.list-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.category-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.category-info {
  flex: 1;
}

.category-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.category-parent {
  font-size: 12px;
  color: #999;
}

.category-sort {
  font-size: 12px;
  color: #666;
  background: #f0f2f5;
  padding: 4px 8px;
  border-radius: 4px;
}

.card-actions {
  display: flex;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f2f5;
}

.btn-edit,
.btn-delete {
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
}

.btn-edit {
  background: #e6f7ff;
  color: #1890ff;
}

.btn-edit:active {
  background: #bae7ff;
}

.btn-delete {
  background: #fff1f0;
  color: #ff4d4f;
}

.btn-delete:active {
  background: #ffccc7;
}

/* 弹窗 */
.mobile-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  z-index: 2000;
  animation: fadeIn 0.3s;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.dialog-content {
  background: white;
  width: 100%;
  border-radius: 16px 16px 0 0;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s;
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

.dialog-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f2f5;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dialog-header h3 {
  margin: 0;
  font-size: 16px;
  color: #333;
  font-weight: 600;
}

.dialog-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.dialog-close:active {
  background: #f0f2f5;
}

.dialog-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
  font-weight: 500;
}

.required {
  color: #ff4d4f;
}

.form-select,
.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  background: white;
  transition: all 0.3s;
  box-sizing: border-box;
}

.form-select:focus,
.form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
}

.form-select.error,
.form-input.error {
  border-color: #ff4d4f;
}

.error-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #ff4d4f;
}

.dialog-footer {
  padding: 16px 20px;
  border-top: 1px solid #f0f2f5;
  display: flex;
  gap: 12px;
}

.btn-cancel,
.btn-confirm {
  flex: 1;
  padding: 14px;
  border-radius: 8px;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
}

.btn-cancel {
  background: #f0f2f5;
  color: #666;
}

.btn-cancel:active {
  background: #e0e3e8;
}

.btn-confirm {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-confirm:active {
  opacity: 0.9;
}

/* 底部导航 */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #e8e8e8;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  z-index: 1000;
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 0;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
}

.nav-item-icon {
  font-size: 20px;
  line-height: 1;
}

.nav-item span:last-child {
  font-size: 10px;
  color: #999;
}

.nav-item.active span:last-child {
  color: #FF6B35;
}

.nav-item:active {
  transform: scale(0.95);
}

/* 响应式 */
@media (min-width: 768px) {
  .sub-category-manage-mobile {
    max-width: 600px;
    margin: 0 auto;
  }
}
</style>
